import { 
  Zap, Brain, BookOpen, Sparkles, Search, Pencil, Scale, Crown, 
  CheckCircle, Layers, ChefHat, Mail, FileText, Lightbulb, GitCompare,
  Target, TrendingUp, Compass, Users, RefreshCw, Lock, Share2, MoreVertical, FolderInput
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { getLensLabel } from "@/lib/agentBuildConstants";
import type { AITool, Agent } from "@/types/aiToolkit";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Zap, Brain, BookOpen, Sparkles, Search, Pencil, Scale, Crown,
  CheckCircle, Layers, ChefHat, Mail, FileText, Lightbulb, GitCompare,
  Target, TrendingUp, Compass, Users, RefreshCw
};

interface AIToolCardProps {
  tool: AITool;
  onClick?: () => void;
  compact?: boolean;
  onShareAgent?: (tool: AITool) => void;
  onMoveAgentToFolder?: (tool: AITool) => void;
  agentFolderName?: string;
}

export function AIToolCard({ tool, onClick, compact = false, onShareAgent, onMoveAgentToFolder, agentFolderName }: AIToolCardProps) {
  const IconComponent = iconMap[tool.icon] || Sparkles;
  const isUserAgent = tool.tier === "agent" && tool.createdBy === "user";
  const userAgent = isUserAgent ? (tool as Agent) : null;
  const lensLabel = userAgent?.lens ? getLensLabel(userAgent.lens) : null;
  const trainingCount =
    (userAgent?.trainingDocumentIds?.length ?? 0) + (userAgent?.linkedSourceIds?.length ?? 0);

  const tierBadgeMap: Record<string, { label: string; color: string }> = {
    'quick-prompt': { label: 'Quick', color: 'bg-green-100 text-green-700 border-green-200' },
    'agent': { label: 'Agent', color: 'bg-purple-100 text-purple-700 border-purple-200' },
    'workflow': { label: 'Workflow', color: 'bg-orange-100 text-orange-700 border-orange-200' }
  };
  const tierBadge = tierBadgeMap[tool.tier] || { label: tool.tier, color: 'bg-gray-100 text-gray-700 border-gray-200' };

  const accessBadge = {
    'free': null,
    'pro': { label: 'Pro', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    'premium': { label: 'Premium', color: 'bg-slate-100 text-slate-700 border-slate-200' }
  }[tool.access];

  if (compact) {
    return (
      <Card 
        className="group cursor-pointer hover:shadow-md transition-all border hover:border-primary/30"
        onClick={onClick}
      >
        <CardContent className="p-3 sm:p-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={cn("w-8 h-8 rounded-md flex items-center justify-center text-white shrink-0", tool.color)}>
                <IconComponent className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm truncate">{tool.name}</span>
                  {accessBadge && (
                    <Lock className="h-3 w-3 text-amber-500 shrink-0" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate mt-0.5">{tool.description}</p>
                {isUserAgent && (lensLabel || agentFolderName || trainingCount > 0) && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {lensLabel && lensLabel !== "None" && (
                      <Badge variant="outline" className="text-[10px] px-1 py-0">
                        {lensLabel}
                      </Badge>
                    )}
                    {agentFolderName && (
                      <Badge variant="outline" className="text-[10px] px-1 py-0">
                        {agentFolderName}
                      </Badge>
                    )}
                    {trainingCount > 0 && (
                      <Badge variant="outline" className="text-[10px] px-1 py-0">
                        {trainingCount} training
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {isUserAgent && (onShareAgent || onMoveAgentToFolder) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      data-testid={`button-agent-menu-${tool.id}`}
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {onMoveAgentToFolder && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onMoveAgentToFolder(tool);
                        }}
                      >
                        <FolderInput className="h-4 w-4 mr-2" />
                        Move to Folder
                      </DropdownMenuItem>
                    )}
                    {onShareAgent && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onShareAgent(tool);
                        }}
                        data-testid={`button-share-agent-${tool.id}`}
                      >
                        <Share2 className="h-4 w-4 mr-2" />
                        Share Agent
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Badge variant="outline" className={cn("text-xs w-fit sm:w-auto", tierBadge.color)}>
                {tierBadge.label}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      className="group cursor-pointer hover:shadow-lg transition-all border hover:border-primary/30 overflow-hidden"
      onClick={onClick}
    >
      <CardContent className="p-0">
        <div className={cn("h-2", tool.color)} />
        <div className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center text-white", tool.color)}>
              <IconComponent className="h-5 w-5" />
            </div>
            <div className="flex gap-1.5 items-center">
              {isUserAgent && (onShareAgent || onMoveAgentToFolder) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      data-testid={`button-agent-menu-${tool.id}`}
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {onMoveAgentToFolder && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onMoveAgentToFolder(tool);
                        }}
                      >
                        <FolderInput className="h-4 w-4 mr-2" />
                        Move to Folder
                      </DropdownMenuItem>
                    )}
                    {onShareAgent && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onShareAgent(tool);
                        }}
                        data-testid={`button-share-agent-${tool.id}`}
                      >
                        <Share2 className="h-4 w-4 mr-2" />
                        Share Agent
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Badge variant="outline" className={cn("text-xs", tierBadge.color)}>
                {tierBadge.label}
              </Badge>
              {accessBadge && (
                <Badge variant="outline" className={cn("text-xs", accessBadge.color)}>
                  {accessBadge.label}
                </Badge>
              )}
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold text-sm group-hover:text-primary transition-colors">
              {tool.name}
            </h3>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {tool.description}
            </p>
          </div>

          <div className="flex flex-wrap gap-1">
            {tool.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface AIToolListProps {
  tools: AITool[];
  onToolClick?: (tool: AITool) => void;
  onShareAgent?: (tool: AITool) => void;
  onMoveAgentToFolder?: (tool: AITool) => void;
  viewMode?: 'grid' | 'list';
  agentFolderNames?: Record<string, string>;
}

export function AIToolList({ tools, onToolClick, onShareAgent, onMoveAgentToFolder, viewMode = 'grid', agentFolderNames }: AIToolListProps) {
  const folderNameFor = (tool: AITool) => {
    if (tool.tier !== "agent" || tool.createdBy !== "user") return undefined;
    const folderId = (tool as Agent).folderId;
    return folderId && agentFolderNames ? agentFolderNames[folderId] : undefined;
  };

  if (viewMode === 'list') {
    return (
      <div className="space-y-2">
        {tools.map((tool) => (
          <AIToolCard 
            key={tool.id} 
            tool={tool} 
            onClick={() => onToolClick?.(tool)}
            onShareAgent={onShareAgent}
            onMoveAgentToFolder={onMoveAgentToFolder}
            agentFolderName={folderNameFor(tool)}
            compact
          />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {tools.map((tool) => (
        <AIToolCard 
          key={tool.id} 
          tool={tool} 
          onClick={() => onToolClick?.(tool)}
          onShareAgent={onShareAgent}
          onMoveAgentToFolder={onMoveAgentToFolder}
          agentFolderName={folderNameFor(tool)}
        />
      ))}
    </div>
  );
}
